import React, {useContext, useRef, useState} from "react";
import AppContext from "../../appContext";
import LimeProgramLabInputRow from "../../controllers/LimeProgramLabInputRow";
import LimeProgramLabInputs from "../../controllers/LimeProgramLabInputs";
import LabInputUnit from "../../controllers/LabInputUnit";
import SampleDepth from "../../controllers/SampleDepth";
import NullableMeasureValue from "../../controllers/NullableMeasureValue";
import MeasurementUnit from "../../controllers/MeasurementUnit";
import {DataTable} from "../../components/DataTable";
import {PlusCircleIcon, ArrowUpOnSquareIcon} from "@heroicons/react/24/solid";
import {arrayPush, arrayRemoveIndex, arrayUpdate} from "../../immutableState";
import Dialog from "../../components/Dialog";
import FileUploader from "../../components/FileUploader";
import Papa from "papaparse";
import {customUnits, unitCustom} from "../program/CustomUnits";
import {getValueNullable} from "../program/Helpers";
import {NullableMeasureValueField} from "../program/MeasureValueField";
import NumberNullable from "../../components/NumberNullable";
import Number from "../../components/Number";
import {onInputHandler} from "../../wrapper";
import {KeyOfType} from "../../components/Fields";
import {ELEMENT_SPECIFIC_GRAVITY} from "./staticdata";
import {parseSampleDepth} from "./LimeSoilSummary";

interface LimeSoilDataDialogProps {
    data: LimeProgramLabInputs;
    onSave: (data: LimeProgramLabInputs) => void;
    onDiscard: () => void;
    labInputUnit?: LabInputUnit;
}

interface RowWithId extends LimeProgramLabInputRow {
    _id: number;
}

// Create a NullableMeasureValue from a number (mg/kg and ppm are 1:1 equivalent)
function toMeasureValue(value: number): NullableMeasureValue {
    return {metric: value, imperial: value};
}

// Convert Uruguay meq/100g values to mg/kg by multiplying by specific gravity
function convertUruguayToMgKg(value: number, element: 'Ca' | 'Mg' | 'K' | 'Na'): number {
    return value * ELEMENT_SPECIFIC_GRAVITY[element];
}

// Convert mg/kg back to Uruguay meq/100g values by dividing by specific gravity
function convertMgKgToUruguay(value: number, element: 'Ca' | 'Mg' | 'K' | 'Na'): number {
    return value / ELEMENT_SPECIFIC_GRAVITY[element];
}

// Check if region uses meq/100g lab input units
function isMeq100g(labInputUnit: LabInputUnit | undefined): boolean {
    return labInputUnit === LabInputUnit.Meq100g;
}

function emptyRow(id: number): RowWithId {
    return {
        _id: id,
        landRef: '',
        referenceNr: '',
        phH2O: 0,
        pBrayI: toMeasureValue(0),
        k: toMeasureValue(0),
        na: toMeasureValue(0),
        ca: toMeasureValue(0),
        mg: toMeasureValue(0),
        exchH: 0,
        sulfur: toMeasureValue(0),
        density: 1.2,
        caPercent: null,
        mgPercent: null,
        kPercent: null,
        naPercent: null,
        acidSat: null,
        sValue: null,
        cec: null,
        caMgRatio: null,
        caMgKRatio: null,
        mgKRatio: null,
        naKRatio: null,
        // Override fields
        limeTypeOverride: null,
        limeNeedOverride: null,
        gypsumNeedOverride: null,
        kNeedOverride: null,
        pNeedOverride: null,
        // GPS coordinates
        latitude: null,
        longitude: null,
        // Sample depth
        sampleDepth: SampleDepth.TopSoil,
        // Calculated outputs (populated after calculation)
        outputs: null
    }
}

let nextId = -1;
function getNextId(): number {
    return nextId--;
}

const LimeSoilDataDialog: React.FC<LimeSoilDataDialogProps> = (props) => {
    const app = useContext(AppContext);
    const system = app.initial.system;
    const [data, setData] = useState<RowWithId[]>(() =>
        props.data.rows.map((row, index) => ({...row, _id: index}))
    );
    const [showUpload, setShowUpload] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [editRow, setEditRow] = useState<RowWithId>(emptyRow(0));
    const [editIndex, setEditIndex] = useState<number>(-1);
    const changed = useRef<boolean>(false);

    // meq/100g regions use different unit for K, Na, Ca, Mg; other regions use mg/kg
    const meq100gMode = isMeq100g(props.labInputUnit);

    // Get the unit label - meq/100g regions show meq/100g, others show mg/kg or ppm
    const unitLabel = meq100gMode ? 'meq/100g' : unitCustom(system, customUnits.mgKg_ppm);

    // Helper for NullableMeasureValue fields (follows EditSoil pattern)
    function nullableMeasure(label: string, key: KeyOfType<RowWithId, NullableMeasureValue>) {
        return <td>
            <div>{label}</div>
            <NullableMeasureValueField
                unit={customUnits.mgKg_ppm}
                value={editRow[key]}
                update={v => setEditRow({...editRow, [key]: v})}
            />
        </td>
    }

    // Helper for nullable number fields
    function nullableNumber(label: string, key: KeyOfType<RowWithId, number | null>) {
        return <td>
            <div>{label}</div>
            <NumberNullable value={editRow[key]} change={v => setEditRow({...editRow, [key]: v})}/>
        </td>
    }

    function handleCsvUpload(file: File) {
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                const rows: RowWithId[] = results.data.map((row: any) => {
                    // Parse values and convert to NullableMeasureValue
                    const pBrayI = parseFloat(row['P Bray I'] || row['P_BrayI'] || row['P'] || row['PBray'] || '0') || 0;
                    // For Uruguay, also check for meq/100g column headers
                    let k = parseFloat(row['K'] || row['K mg/kg'] || row['K (mg/kg)'] || row['K (ppm)'] || row['K (meq/100g)'] || '0') || 0;
                    let na = parseFloat(row['Na'] || row['Na mg/kg'] || row['Na (mg/kg)'] || row['Na (ppm)'] || row['Na (meq/100g)'] || '0') || 0;
                    let ca = parseFloat(row['Ca'] || row['Ca mg/kg'] || row['Ca (mg/kg)'] || row['Ca (ppm)'] || row['Ca (meq/100g)'] || '0') || 0;
                    let mg = parseFloat(row['Mg'] || row['Mg mg/kg'] || row['Mg (mg/kg)'] || row['Mg (ppm)'] || row['Mg (meq/100g)'] || '0') || 0;
                    const sulfur = parseFloat(row['S'] || row['Sulfur'] || row['S (mg/kg)'] || row['S (ppm)'] || '0') || 0;

                    // Uruguay: Convert meq/100g to mg/kg for storage
                    if (meq100gMode) {
                        k = convertUruguayToMgKg(k, 'K');
                        na = convertUruguayToMgKg(na, 'Na');
                        ca = convertUruguayToMgKg(ca, 'Ca');
                        mg = convertUruguayToMgKg(mg, 'Mg');
                    }

                    return {
                        _id: getNextId(),
                        landRef: row['Land Ref'] || row['LandRef'] || row['Block'] || row['Land ref'] || '',
                        referenceNr: row['Reference Nr'] || row['Reference'] || row['ReferenceNr'] || row['Ref'] || row['Sample#'] || row['Reference nr.'] || '',
                        phH2O: parseFloat(row['pH(H2O)'] || row['pH (H2O)'] || row['pH_H2O'] || row['pH'] || '0') || 0,
                        pBrayI: toMeasureValue(pBrayI),
                        k: toMeasureValue(k),
                        na: toMeasureValue(na),
                        ca: toMeasureValue(ca),
                        mg: toMeasureValue(mg),
                        exchH: parseFloat(row['Exch H'] || row['Exch H+'] || row['ExchH'] || row['H'] || row['OUT H+ (cmol(+)/kg)'] || '0') || 0,
                        sulfur: toMeasureValue(sulfur),
                        density: parseFloat(row['Density'] || row['BD'] || row['Density (g/cm3)'] || '1.2') || 1.2,
                        // Excel uses %Ca format, we also support Ca (%) and Ca%
                        caPercent: parseFloat(row['%Ca'] || row['Ca (%)'] || row['Ca%'] || '') || null,
                        mgPercent: parseFloat(row['%Mg'] || row['Mg (%)'] || row['Mg%'] || '') || null,
                        kPercent: parseFloat(row['%K'] || row['K (%)'] || row['K%'] || '') || null,
                        naPercent: parseFloat(row['%Na'] || row['Na (%)'] || row['Na%'] || '') || null,
                        acidSat: parseFloat(row['Acid Sat'] || row['Acid Sat.'] || row['AcidSat'] || row['SOUR.V (%)'] || '') || null,
                        sValue: parseFloat(row['S-Value'] || row['SValue'] || row['S-Value (cmol(+)/kg)'] || '') || null,
                        cec: parseFloat(row['CEC'] || row['T'] || row['T (cmol(+)/kg)'] || '') || null,
                        caMgRatio: parseFloat(row['Ca:Mg'] || row['CaMgRatio'] || row['Ca:Mg (1.5-4.5)'] || '') || null,
                        caMgKRatio: parseFloat(row['(Ca+Mg)/K'] || row['CaMgKRatio'] || row['(Ca+Mg)/K (10.0-20.0)'] || '') || null,
                        mgKRatio: parseFloat(row['Mg:K'] || row['MgKRatio'] || row['Mg:K (3.0-4.0)'] || '') || null,
                        naKRatio: parseFloat(row['Na:K'] || row['NaKRatio'] || '') || null,
                        // Override fields from CSV
                        limeTypeOverride: row['Lime Type Override'] || row['LimeTypeOverride'] || null,
                        limeNeedOverride: parseFloat(row['Lime Need Override'] || row['LimeNeedOverride'] || '') || null,
                        gypsumNeedOverride: parseFloat(row['Gypsum Need Override'] || row['GypsumNeedOverride'] || '') || null,
                        kNeedOverride: parseFloat(row['K Need Override'] || row['KNeedOverride'] || '') || null,
                        pNeedOverride: parseFloat(row['P Need Override'] || row['PNeedOverride'] || '') || null,
                        // GPS coordinates
                        latitude: parseFloat(row['Latitude'] || row['Lat'] || row['Y'] || '') || null,
                        longitude: parseFloat(row['Longitude'] || row['Long'] || row['Lon'] || row['X'] || '') || null,
                        // Sample depth
                        sampleDepth: parseSampleDepth(row['Sample Depth'] || row['SampleDepth'] || ''),
                        // Calculated outputs (populated after calculation)
                        outputs: null
                    };
                });

                setData(prev => [...prev, ...rows]);
                changed.current = true;
                setShowUpload(false);
            }
        });
    }

    function addRow() {
        setEditRow(emptyRow(getNextId()));
        setEditIndex(-1);
        setShowEdit(true);
    }

    function onEdit(row: RowWithId, index: number) {
        // For Uruguay, convert stored mg/kg values back to meq/100g for editing
        let editableRow = {...row};
        if (meq100gMode) {
            editableRow = {
                ...row,
                k: toMeasureValue(convertMgKgToUruguay(row.k.metric || 0, 'K')),
                na: toMeasureValue(convertMgKgToUruguay(row.na.metric || 0, 'Na')),
                ca: toMeasureValue(convertMgKgToUruguay(row.ca.metric || 0, 'Ca')),
                mg: toMeasureValue(convertMgKgToUruguay(row.mg.metric || 0, 'Mg'))
            };
        }
        setEditRow(editableRow);
        setEditIndex(index);
        setShowEdit(true);
    }

    function onRemove(index: number) {
        setData(arrayRemoveIndex(data, index));
        changed.current = true;
    }

    function saveEdit() {
        // For Uruguay, convert meq/100g values to mg/kg before saving
        let rowToSave = editRow;
        if (meq100gMode) {
            const kValue = editRow.k.metric || 0;
            const naValue = editRow.na.metric || 0;
            const caValue = editRow.ca.metric || 0;
            const mgValue = editRow.mg.metric || 0;

            rowToSave = {
                ...editRow,
                k: toMeasureValue(convertUruguayToMgKg(kValue, 'K')),
                na: toMeasureValue(convertUruguayToMgKg(naValue, 'Na')),
                ca: toMeasureValue(convertUruguayToMgKg(caValue, 'Ca')),
                mg: toMeasureValue(convertUruguayToMgKg(mgValue, 'Mg'))
            };
        }

        if (editIndex === -1) {
            setData(arrayPush(data, rowToSave));
        } else {
            setData(arrayUpdate(data, editIndex, rowToSave));
        }
        changed.current = true;
        setShowEdit(false);
    }

    function formatValue(value: number | null): string {
        if (value === null) return '-';
        return value.toFixed(2);
    }

    function formatMeasure(value: NullableMeasureValue): string {
        const v = getValueNullable(system, value);
        if (v === null) return '-';
        return v.toFixed(2);
    }

    // Format K, Na, Ca, Mg values - converts back to meq/100g for Uruguay display
    function formatMeasureElement(value: NullableMeasureValue, element: 'Ca' | 'Mg' | 'K' | 'Na'): string {
        const v = getValueNullable(system, value);
        if (v === null) return '-';
        // For Uruguay, convert stored mg/kg back to meq/100g for display
        const displayValue = meq100gMode ? convertMgKgToUruguay(v, element) : v;
        return displayValue.toFixed(2);
    }

    return <>
        <Dialog
            title={app.word('upload_csv')}
            show={showUpload}
            setShow={setShowUpload}
            body={
                <div className="p-4">
                    <div>{app.word('download_csv_sample')}, {app.word('add_data_and_upload_again')}.</div>
                    <a className="underline" href="/LimeSoilSample.csv" download="LimeSoilSample.csv">LimeSoilSample.csv</a>
                    <div className="my-2">
                        <FileUploader onChange={file => handleCsvUpload(file)} fileTypes=".csv"/>
                    </div>
                </div>
            }
        />

        <Dialog
            title={editIndex === -1 ? app.word('add') : app.word('edit')}
            show={showEdit}
            setShow={setShowEdit}
            body={
                <div className="p-4 max-h-[70vh] overflow-y-auto">
                    <table className="w-full">
                        <tbody>
                            <tr>
                                <td>
                                    <div>{app.word('land_ref')}</div>
                                    <input className="input" type="text" value={editRow.landRef}
                                           onInput={onInputHandler(v => setEditRow({...editRow, landRef: v}))}/>
                                </td>
                                <td>
                                    <div>{app.word('reference_nr')}</div>
                                    <input className="input" type="text" value={editRow.referenceNr}
                                           onInput={onInputHandler(v => setEditRow({...editRow, referenceNr: v}))}/>
                                </td>
                                {nullableNumber(app.word('latitude'), 'latitude')}
                                {nullableNumber(app.word('longitude'), 'longitude')}
                                <td>
                                    <div>{app.word('sample_depth')}</div>
                                    <select
                                        className="input"
                                        value={editRow.sampleDepth}
                                        onChange={e => setEditRow({...editRow, sampleDepth: e.target.value as SampleDepth})}
                                    >
                                        <option value={SampleDepth.TopSoil}>{app.word(SampleDepth.TopSoil)}</option>
                                        <option value={SampleDepth.SubSoil}>{app.word(SampleDepth.SubSoil)}</option>
                                        <option value={SampleDepth.TopSoilMicros}>{app.word(SampleDepth.TopSoilMicros)}</option>
                                    </select>
                                </td>
                                <td>
                                    <div>pH(H2O)</div>
                                    <Number value={editRow.phH2O} change={v => setEditRow({...editRow, phH2O: v})}/>
                                </td>
                            </tr>

                            <tr>
                                {nullableMeasure('P Bray I', 'pBrayI')}
                                {nullableMeasure(`K (${unitLabel})`, 'k')}
                                {nullableMeasure(`Na (${unitLabel})`, 'na')}
                                {nullableMeasure(`Ca (${unitLabel})`, 'ca')}
                                {nullableMeasure(`Mg (${unitLabel})`, 'mg')}
                                <td>
                                    <div>Exch H+</div>
                                    <Number value={editRow.exchH} change={v => setEditRow({...editRow, exchH: v})}/>
                                </td>
                            </tr>

                            <tr>
                                {nullableMeasure(app.word('sulfur'), 'sulfur')}
                                <td>
                                    <div>{app.word('density')}</div>
                                    <Number value={editRow.density} change={v => setEditRow({...editRow, density: v})}/>
                                </td>
                                {nullableNumber('%Ca', 'caPercent')}
                                {nullableNumber('%Mg', 'mgPercent')}
                                {nullableNumber('%K', 'kPercent')}
                                {nullableNumber('%Na', 'naPercent')}
                            </tr>

                            <tr>
                                {nullableNumber(app.word('acid_saturated'), 'acidSat')}
                                {nullableNumber('Ca:Mg', 'caMgRatio')}
                                {nullableNumber('(Ca+Mg)/K', 'caMgKRatio')}
                                {nullableNumber('Mg:K', 'mgKRatio')}
                                {nullableNumber('S-Value', 'sValue')}
                                {nullableNumber('Na:K', 'naKRatio')}
                            </tr>

                            <tr>
                                {nullableNumber('CEC', 'cec')}
                            </tr>
                        </tbody>
                    </table>

                    <div className="flex justify-end items-end">
                        <button className="m-2 p-2 text-white rounded-xl bg-red-500" onClick={() => setShowEdit(false)}>
                            {app.word('discard')}
                        </button>
                        <button className="m-2 p-2 text-white rounded-xl bg-primary" onClick={saveEdit}>
                            {app.word('save')}
                        </button>
                    </div>
                </div>
            }
        />

        <div className="my-2">
            {data.length > 0 ? (
                <>
                    <div className="text-xl text-primary mb-2">{app.word('soil_data')}</div>
                    <div className="overflow-x-auto">
                        <DataTable
                            headerClass="px-2 py-2 text-left text-xs font-semibold bg-gray-200"
                            dataClass="whitespace-nowrap px-1 py-2 text-xs text-gray-600"
                            keyExtractor={(row: RowWithId) => row._id}
                            definitions={[
                                {header: app.word('land_ref'), row: r => r.landRef},
                                {header: app.word('reference_nr'), row: r => r.referenceNr},
                                {header: app.word('latitude'), row: r => formatValue(r.latitude)},
                                {header: app.word('longitude'), row: r => formatValue(r.longitude)},
                                {header: app.word('sample_depth'), row: r => app.word(r.sampleDepth)},
                                {header: 'pH(H2O)', row: r => formatValue(r.phH2O)},
                                {header: `P Bray I`, row: r => formatMeasure(r.pBrayI)},
                                {header: `K (${unitLabel})`, row: r => formatMeasureElement(r.k, 'K')},
                                {header: `Na (${unitLabel})`, row: r => formatMeasureElement(r.na, 'Na')},
                                {header: `Ca (${unitLabel})`, row: r => formatMeasureElement(r.ca, 'Ca')},
                                {header: `Mg (${unitLabel})`, row: r => formatMeasureElement(r.mg, 'Mg')},
                                {header: 'Exch H', row: r => formatValue(r.exchH)},
                                {header: '%Ca', row: r => formatValue(r.caPercent)},
                                {header: '%Mg', row: r => formatValue(r.mgPercent)},
                                {header: '%K', row: r => formatValue(r.kPercent)},
                                {header: '%Na', row: r => formatValue(r.naPercent)},
                                {header: app.word('acid_saturated'), row: r => formatValue(r.acidSat)},
                                {header: 'Ca:Mg', row: r => formatValue(r.caMgRatio)},
                                {header: '(Ca+Mg)/K', row: r => formatValue(r.caMgKRatio)},
                                {header: 'Mg:K', row: r => formatValue(r.mgKRatio)},
                                {header: 'S-Value', row: r => formatValue(r.sValue)},
                                {header: 'Na:K', row: r => formatValue(r.naKRatio)},
                                {header: 'CEC', row: r => formatValue(r.cec)},
                                {header: app.word('density'), row: r => formatValue(r.density)},
                                {header: `${app.word('sulfur')} (${unitLabel})`, row: r => formatMeasure(r.sulfur)},
                                {
                                    header: app.word('actions'),
                                    row: (r, index) => <div>
                                        <span className="underline cursor-pointer text-primary"
                                              onClick={() => onRemove(index)}>{app.word('remove')}</span>
                                        <span className="px-2">|</span>
                                        <span className="underline cursor-pointer text-primary"
                                              onClick={() => onEdit(r, index)}>{app.word('edit')}</span>
                                    </div>
                                }
                            ]}
                            data={data}
                        />
                    </div>
                </>
            ) : (
                <div>{app.word('no_soil_data')}</div>
            )}

            <div className="flex items-center mt-2">
                <span title={app.word('add')} className="mr-2">
                    <PlusCircleIcon className="w-6 h-6 cursor-pointer" onClick={addRow}/>
                </span>
                <span title={app.word('upload_csv')}>
                    <ArrowUpOnSquareIcon className="w-6 h-6 cursor-pointer" onClick={() => setShowUpload(true)}/>
                </span>
            </div>
        </div>

        <div className="border-t mt-4 pt-4">
            <div className="flex justify-end">
                <button className="m-2 p-2 text-white rounded-xl bg-red-500" onClick={() => props.onDiscard()}>
                    {app.word('discard')}
                </button>
                <button className="m-2 p-2 text-white rounded-xl bg-primary" onClick={() => {
                    if (changed.current) {
                        // Strip out the _id field before saving
                        const rowsWithoutId: LimeProgramLabInputRow[] = data.map(({_id, ...row}) => row);
                        props.onSave({rows: rowsWithoutId});
                    } else {
                        props.onDiscard();
                    }
                }}>
                    {app.word('save')}
                </button>
            </div>
        </div>
    </>;
};

export default LimeSoilDataDialog;
